import { Box, Flex, Text } from "@chakra-ui/react";
import { debounce } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { NumericInput } from "../input/NumericInput";

export type NumberRangeConfig = {
  onFromChange: (value?: number) => void | Promise<void>;
  onToChange: (value?: number) => void | Promise<void>;
  from?: number | null;
  to?: number | null;
};

export const NumberRangeFilter = ({
  columnLabel,
  config,
  debounceMs,
}: {
  columnLabel: string;
  config: NumberRangeConfig;
  debounceMs: number;
}) => {
  const { t } = useTranslation();
  const from = useBoundFollowingTheQuery(config.from, config.onFromChange, debounceMs);
  const to = useBoundFollowingTheQuery(config.to, config.onToChange, debounceMs);

  return (
    <Box px={3} py={2} borderBottom="1px solid" borderColor="smBorder">
      <Text fontSize="sm" fontWeight="medium" mb={2} color="smMuted">
        {t("Range")}
      </Text>
      <Flex gap={2}>
        <NumericInput
          size="sm"
          placeholder={t("From") ?? undefined}
          aria-label={t("{{column}}, from", { column: columnLabel }) ?? undefined}
          numericValue={from.value}
          onNumericChange={from.change}
        />
        <NumericInput
          size="sm"
          placeholder={t("To") ?? undefined}
          aria-label={t("{{column}}, to", { column: columnLabel }) ?? undefined}
          numericValue={to.value}
          onNumericChange={to.change}
        />
      </Flex>
    </Box>
  );
};

const useBoundFollowingTheQuery = (
  applied: number | null | undefined,
  onChange: (value?: number) => void | Promise<void>,
  debounceMs: number,
) => {
  const [value, setValue] = useState<number | null>(applied ?? null);
  const latestOnChange = useRef(onChange);
  latestOnChange.current = onChange;
  const send = useMemo(
    () =>
      debounce((next?: number) => {
        void latestOnChange.current(next);
      }, debounceMs),
    [debounceMs],
  );

  useEffect(() => {
    send.cancel();
    setValue(applied ?? null);
  }, [applied, send]);
  useEffect(
    () => () => {
      send.flush();
    },
    [send],
  );

  const change = (next: number | null) => {
    setValue(next);
    send(next ?? undefined);
  };
  return { value, change };
};
